import { Button, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { useNavigate } from "react-router";
import { usePermission } from "@how/app-kit";
import { DataTable, PageHeader, StatusBadge } from "@how/ui";
import { WebsiteTabs } from "./WebsiteTabs";
import {
  PAGE_STATUS_LABEL,
  pagePath,
  pageStatus,
  useCreatePage,
  usePages,
  type PageRow,
} from "./website-queries";
import { slugify } from "./slug";

const columns: ColumnDef<PageRow>[] = [
  {
    accessorKey: "title",
    header: "Page",
    enableSorting: false,
    cell: (ctx) => (
      <Text size="sm" fw={600}>
        {ctx.row.original.title}
      </Text>
    ),
  },
  {
    id: "address",
    header: "Address",
    enableSorting: false,
    cell: (ctx) => (
      <Text size="sm" c="dimmed" ff="monospace">
        {pagePath(ctx.row.original.published_slug ?? ctx.row.original.slug)}
      </Text>
    ),
  },
  {
    id: "status",
    header: "Status",
    enableSorting: false,
    cell: (ctx) => {
      const status = pageStatus(ctx.row.original);
      return <StatusBadge domain="page_status" value={status} label={PAGE_STATUS_LABEL[status]} />;
    },
  },
  {
    id: "published",
    header: "Last published",
    enableSorting: false,
    cell: (ctx) =>
      ctx.row.original.published_at === null ? (
        <Text size="sm" c="dimmed">
          Never
        </Text>
      ) : (
        <Text size="sm">{new Date(ctx.row.original.published_at).toLocaleString()}</Text>
      ),
  },
];

const PAGE = { pageIndex: 0, pageSize: 200 };

/** The church's website pages. Pages with unpublished changes are the review list (ADR 0032). */
export function WebsitePagesPage() {
  const navigate = useNavigate();
  const pages = usePages();
  const canManage = usePermission("website.manage", { write: true });
  const createPage = useCreatePage();
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const status = pages.isPending ? "loading" : pages.isError ? "error" : "ready";
  const taken = (createPage.error as { code?: string } | null)?.code === "23505";

  const close = () => {
    setAdding(false);
    setTitle("");
    setSlug("");
    setSlugTouched(false);
    createPage.reset();
  };

  return (
    <>
      <PageHeader
        title="Website"
        description="The pages on your church's public website. Changes stay private until someone publishes them."
        actions={
          canManage.data === true ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setAdding(true);
              }}
            >
              Add page
            </Button>
          ) : undefined
        }
      />
      <WebsiteTabs active="pages" />
      <DataTable
        label="Pages"
        columns={columns}
        rows={pages.data ?? []}
        totalRows={pages.data?.length ?? 0}
        status={status}
        pagination={PAGE}
        onPaginationChange={() => undefined}
        sorting={[]}
        onSortingChange={() => undefined}
        getRowId={(row) => row.id}
        onRowClick={(row) => {
          void navigate(`/website/pages/${row.id}`);
        }}
        emptyTitle="No pages yet"
        emptyDescription="Add a home page to start your church's website."
        errorMessage="Could not load pages."
        onRetry={() => {
          void pages.refetch();
        }}
      />
      <Modal opened={adding} onClose={close} title="Add a page" radius="lg">
        <Stack gap="sm">
          <TextInput
            label="Title"
            required
            value={title}
            onChange={(event) => {
              const next = event.currentTarget.value;
              setTitle(next);
              if (!slugTouched) setSlug(slugify(next));
            }}
          />
          <TextInput
            label="Address"
            description="Lowercase words joined by hyphens. Use home for the home page."
            required
            leftSection={<Text size="sm">/</Text>}
            value={slug}
            error={taken ? "Another page already uses this address." : undefined}
            onChange={(event) => {
              const next = event.currentTarget.value;
              setSlugTouched(true);
              setSlug(next);
            }}
          />
          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" onClick={close}>
              Cancel
            </Button>
            <Button
              disabled={title.trim().length === 0 || slug.trim().length === 0}
              loading={createPage.isPending}
              onClick={() => {
                createPage.mutate(
                  { title: title.trim(), slug: slug.trim() },
                  {
                    onSuccess: (id) => {
                      notifications.show({
                        color: "teal",
                        title: "Page added",
                        message: title.trim(),
                      });
                      close();
                      void navigate(`/website/pages/${id}`);
                    },
                  },
                );
              }}
            >
              Add page
            </Button>
          </Group>
          {createPage.isError && !taken ? (
            <Text size="sm" c="red">
              The page was not added. Check the address uses only lowercase letters, numbers and
              hyphens.
            </Text>
          ) : null}
        </Stack>
      </Modal>
    </>
  );
}
